import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateOnly, dateTime, downloadCsv, humanize, type CsvColumn } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input } from '@/components/ui/primitives';
import { Pager, ProductSearch, StockStatusBadge, formatQty, productTitle, signedQty } from './stock-control-common';
import { MOVEMENT_PAGE_SIZE, type ProductHit, type RecallMovement, type RecallReport } from './stock-control-types';

interface ReportRequest {
  productId: string;
  batchNo: string;
}

const MOVEMENT_COLUMNS: CsvColumn<RecallMovement>[] = [
  { header: 'Date', value: (movement) => movement.at },
  { header: 'Type', value: (movement) => movement.type },
  { header: 'Quantity', value: (movement) => movement.qty },
  { header: 'Store', value: (movement) => movement.store },
  { header: 'Document number', value: (movement) => movement.documentNo },
  { header: 'Document type', value: (movement) => movement.documentType },
  { header: 'Counterparty', value: (movement) => movement.counterparty },
  { header: 'Contact', value: (movement) => movement.contact },
  { header: 'Reason', value: (movement) => movement.reason },
];

function exportName(report: RecallReport): string {
  return `recall-${report.product.code}-${report.batchNo}`.replace(/[^A-Za-z0-9_-]+/g, '_');
}

export function RecallReportCard() {
  const [product, setProduct] = useState<ProductHit | null>(null);
  const [batchNo, setBatchNo] = useState('');
  const [request, setRequest] = useState<ReportRequest | null>(null);
  const [offset, setOffset] = useState(0);

  const report = useQuery({
    queryKey: ['stock-control-recall-report', request?.productId ?? '', request?.batchNo ?? '', offset],
    queryFn: () =>
      get<RecallReport>(
        `/pharmacy/stock/recall-report${qs({
          productId: request?.productId,
          batchNo: request?.batchNo,
          limit: MOVEMENT_PAGE_SIZE,
          offset,
        })}`,
      ),
    enabled: request !== null,
    placeholderData: keepPreviousData,
  });

  const canRun = product !== null && batchNo.trim() !== '';

  const runReport = () => {
    if (!product || batchNo.trim() === '') return;
    setOffset(0);
    setRequest({ productId: product.id, batchNo: batchNo.trim() });
  };

  const data = report.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{tr('pharmacy', 'recall_report')}</CardTitle>
        <CardDescription>
          {tr('pharmacy', 'pick_a_product_and_type_a_batch_number_to_see')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label={tr('pharmacy', 'product')} required>
            <ProductSearch
              selected={product}
              onSelect={(next) => {
                setProduct(next);
                setRequest(null);
              }}
            />
          </Field>
          <Field label={tr('pharmacy', 'batch_number')} htmlFor="recall-batch-no" required>
            <div className="flex gap-2">
              <Input
                id="recall-batch-no"
                value={batchNo}
                maxLength={64}
                placeholder={tr('pharmacy', 'batch_number_then_press_enter')}
                onChange={(event) => setBatchNo(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  runReport();
                }}
              />
              <Button onClick={runReport} disabled={!canRun} loading={report.isFetching && !report.isPlaceholderData}>
                {tr('pharmacy', 'run_report')}
              </Button>
            </div>
          </Field>
        </div>

        {request !== null && report.error && <Alert variant="destructive">{describeError(report.error)}</Alert>}

        {request !== null && data && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm">
                <span className="font-semibold">{productTitle(data.product)}</span>
                <span className="ms-2 font-mono text-xs text-muted-foreground">{data.product.code}</span>
                <span className="ms-2 text-muted-foreground">
                  {tr('pharmacy', 'batch_3')} <span className="font-mono">{data.batchNo}</span>
                </span>
              </p>
              <Button
                variant="outline"
                size="sm"
                disabled={data.movements.length === 0}
                onClick={() => downloadCsv(exportName(data), data.movements, MOVEMENT_COLUMNS)}
              >
                <Download className="h-4 w-4" /> {tr('pharmacy', 'export_movements_csv')}
              </Button>
            </div>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold">{tr('pharmacy', 'held_now')}</h3>
              <DataTable
                dense
                rows={data.batches}
                rowKey={(batch) => batch.id}
                empty={tr('pharmacy', 'this_batch_is_not_held_in_any_store')}
                columns={[
                  { header: tr('pharmacy', 'store'), cell: (batch) => batch.store.name },
                  { header: tr('pharmacy', 'expires'), cell: (batch) => dateOnly(batch.expiryDate) },
                  { header: tr('pharmacy', 'on_hand'), align: 'right', cell: (batch) => `${formatQty(batch.qtyOnHand)} ${data.product.unit}` },
                  { header: tr('pharmacy', 'status'), cell: (batch) => <StockStatusBadge status={batch.status} /> },
                  { header: tr('pharmacy', 'reason'), cell: (batch) => batch.statusReason ?? '—' },
                ]}
              />
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold">{tr('pharmacy', 'received_from')}</h3>
              <DataTable
                dense
                rows={data.receivedFrom}
                rowKey={(receipt) => `${receipt.grnId}-${receipt.qty}`}
                empty={tr('pharmacy', 'no_goods_receipt_is_on_record_for_this_batch')}
                columns={[
                  {
                    header: tr('pharmacy', 'supplier'),
                    cell: (receipt) => (
                      <div>
                        <p className="font-medium">{receipt.supplier.name}</p>
                        {receipt.supplier.phone && <p className="text-xs text-muted-foreground">{receipt.supplier.phone}</p>}
                      </div>
                    ),
                  },
                  { header: tr('pharmacy', 'receipt'), cell: (receipt) => <span className="font-mono text-xs">{receipt.grnNumber}</span> },
                  { header: tr('pharmacy', 'supplier_invoice'), cell: (receipt) => receipt.supplierInvoiceNo ?? '—' },
                  { header: tr('pharmacy', 'received'), cell: (receipt) => dateOnly(receipt.receivedAt) },
                  { header: tr('pharmacy', 'qty_2'), align: 'right', cell: (receipt) => formatQty(receipt.qty) },
                ]}
              />
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold">{tr('pharmacy', 'totals_by_movement_type')}</h3>
              <DataTable
                dense
                rows={data.totalsByType}
                rowKey={(total) => total.type}
                empty={tr('pharmacy', 'no_movements_yet')}
                columns={[
                  { header: tr('pharmacy', 'type'), cell: (total) => humanize(total.type) },
                  { header: tr('pharmacy', 'movements'), align: 'right', cell: (total) => total.movements },
                  { header: tr('pharmacy', 'net_quantity'), align: 'right', cell: (total) => signedQty(total.qty) },
                ]}
              />
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold">{tr('pharmacy', 'where_it_went')}</h3>
              <DataTable
                dense
                rows={data.movements}
                rowKey={(movement) => `${movement.at}-${movement.type}-${movement.documentNo ?? ''}-${movement.qty}-${movement.store}`}
                empty={tr('pharmacy', 'no_sales_issues_or_transfers_on_this_page')}
                columns={[
                  { header: tr('pharmacy', 'date'), cell: (movement) => dateTime(movement.at) },
                  { header: tr('pharmacy', 'type'), cell: (movement) => humanize(movement.type) },
                  { header: tr('pharmacy', 'qty_2'), align: 'right', cell: (movement) => signedQty(movement.qty) },
                  { header: tr('pharmacy', 'store'), cell: (movement) => movement.store },
                  {
                    header: tr('pharmacy', 'document'),
                    cell: (movement) => (
                      <div>
                        <p className="font-mono text-xs">{movement.documentNo ?? '—'}</p>
                        {movement.documentType && <p className="text-xs text-muted-foreground">{humanize(movement.documentType)}</p>}
                      </div>
                    ),
                  },
                  { header: tr('pharmacy', 'patient_ward_store_or_supplier'), cell: (movement) => movement.counterparty ?? '—' },
                  { header: tr('pharmacy', 'contact'), cell: (movement) => movement.contact ?? '—' },
                ]}
              />
              <Pager
                offset={offset}
                pageSize={MOVEMENT_PAGE_SIZE}
                rowCount={data.movements.length}
                disabled={report.isFetching}
                onOffsetChange={setOffset}
              />
              <p className="text-xs text-muted-foreground">
                {tr('pharmacy', 'the_export_contains_the_movements_on_this_page')}
              </p>
            </section>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
